import { BaseCanvasPack } from "./BaseCanvasPack";

export default class IcyCanvasPack extends BaseCanvasPack {
  /**
   * Draws the canvas background with icy theme
   */
  drawBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number,
    level?: number
  ): void {
    // Animated icy gradients
    let offset = 0;
    if (frameTime !== undefined) {
      offset = Math.sin(frameTime / 2000) * 5;
    }

    // Base fill
    const baseGradient = context.createLinearGradient(0, 0, width, height);
    baseGradient.addColorStop(0, "rgba(5, 15, 25, 0.8)");
    baseGradient.addColorStop(0.25, "rgba(15, 30, 45, 0.6)");
    baseGradient.addColorStop(0.5, "rgba(25, 45, 65, 0.5)");
    baseGradient.addColorStop(0.75, "rgba(15, 30, 45, 0.6)");
    baseGradient.addColorStop(1, "rgba(5, 15, 25, 0.8)");
    
    context.fillStyle = baseGradient;
    context.fillRect(0, 0, width, height);

    // Radial glows
    const drawGlow = (x: number, y: number, r: number, colorStart: string, colorEnd: string) => {
      const g = context.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, colorStart);
      g.addColorStop(0.3, colorEnd);
      g.addColorStop(0.6, "transparent");
      context.fillStyle = g;
      context.fillRect(0, 0, width, height);
    };

    drawGlow(width * (0.2 + offset/100), height * (0.3 + offset/100), width * 0.6, "rgba(176, 224, 230, 0.4)", "rgba(176, 224, 230, 0.15)");
    drawGlow(width * (0.8 - offset/100), height * (0.4 - offset/100), width * 0.6, "rgba(135, 206, 250, 0.35)", "rgba(135, 206, 250, 0.12)");
    drawGlow(width * (0.5 + offset/100), height * (0.7 - offset/100), width * 0.6, "rgba(173, 216, 230, 0.3)", "rgba(173, 216, 230, 0.1)");

    if (frameTime !== undefined) {
      this.drawSnow(context, width, height, frameTime, true);
    }
  }

  /**
   * Draws the canvas frame with icy theme - frozen icicles and frost
   */
  drawFrame(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    // Base frozen frame (gradient for ice effect)
    const borderGradient = context.createLinearGradient(0, 0, width, height);
    borderGradient.addColorStop(0, "#E0F6FF");   // White/Cyan
    borderGradient.addColorStop(0.2, "#B0E0E6"); // Powder Blue
    borderGradient.addColorStop(0.5, "#00CED1"); // Dark Turquoise
    borderGradient.addColorStop(0.8, "#B0E0E6"); // Powder Blue
    borderGradient.addColorStop(1, "#E0F6FF");   // White/Cyan

    const borderWidth = 4;
    
    // Draw main border rectangle with gradient
    context.strokeStyle = borderGradient;
    context.lineWidth = borderWidth;
    context.lineJoin = "round";
    context.strokeRect(borderWidth / 2, borderWidth / 2, width - borderWidth, height - borderWidth);

    // Draw icicles hanging from the top
    this.drawIcicles(context, width, borderWidth);

    // Draw frozen pillars on sides and bottom accumulation
    this.drawFrozenPillars(context, width, height, borderWidth);
  }

  /**
   * Draws overlay effects on top of game pieces
   */
  drawOverlay(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    // Add animated glimmer
    if (frameTime !== undefined) {
      this.drawIceGlimmer(context, width, height, frameTime);
    }
    
    // Draw foreground snow (on top of everything)
    if (frameTime !== undefined) {
      this.drawSnow(context, width, height, frameTime, false);
    }
  }

  /**
   * Draws hanging icicles along the top edge
   */
  private drawIcicles(
    context: CanvasRenderingContext2D,
    width: number,
    offset: number
  ): void {
    context.save();
    context.fillStyle = "rgba(224, 246, 255, 0.95)"; // Opaque whitish cyan
    context.shadowColor = "rgba(255, 255, 255, 0.6)";
    context.shadowBlur = 4;
    
    context.beginPath();
    context.moveTo(0, 0);
    
    // Generate sharp icicles
    const segmentWidth = 8; // Narrower for sharper look
    for (let x = 0; x <= width; x += segmentWidth) {
      // Deterministic random height
      // Use multiple sine waves for variety
      const noise = Math.abs(Math.sin(x * 132.5) + Math.sin(x * 44.2) * 0.5);
      const h = 5 + 25 * noise;
      
      // Top point
      context.lineTo(x, 0);
      // Tip of icicle (sharp point)
      context.lineTo(x + segmentWidth / 2, h);
    }
    
    context.lineTo(width, 0);
    context.closePath();
    context.fill();
    context.restore();
  }

  /**
   * Draws frozen pillars on sides and accumulation on bottom
   */
  private drawFrozenPillars(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    offset: number
  ): void {
    context.save();
    
    // 1. Bottom frost accumulation (snow-like)
    context.fillStyle = "rgba(176, 224, 230, 0.5)"; // Semi-transparent frost
    context.beginPath();
    context.moveTo(0, height);
    const segmentWidth = 10;
    for (let x = 0; x <= width; x += segmentWidth) {
      // Soft wavy bottom edge
      const h = 5 + 8 * Math.abs(Math.sin(x * 0.05) + Math.sin(x * 0.1) * 0.5);
      context.lineTo(x, height - h);
    }
    context.lineTo(width, height);
    context.lineTo(0, height);
    context.closePath();
    context.fill();

    // 2. Side Frozen Pillars
    const pillarWidth = 10; // Reduced width (was 16)
    
    // Left Pillar
    this.drawIcePillar(context, 0, 0, pillarWidth, height);
    
    // Right Pillar
    this.drawIcePillar(context, width - pillarWidth, 0, pillarWidth, height);
    
    context.restore();
  }

  /**
   * Draws a single vertical ice pillar with irregular, jagged shape
   */
  private drawIcePillar(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number
  ): void {
    const segmentHeight = 10;
    
    context.save();
    context.beginPath();
    
    // Start top-left
    context.moveTo(x, y);
    
    // Draw left edge (jagged)
    for (let curY = 0; curY <= h; curY += segmentHeight) {
      // Deterministic wobble based on Y and X (so left/right pillars differ)
      const noise = Math.sin(curY * 0.1 + x) * 2; 
      context.lineTo(x + noise, curY);
    }
    
    // Bottom edge
    context.lineTo(x + w, h);
    
    // Draw right edge (jagged, going up)
    for (let curY = h; curY >= 0; curY -= segmentHeight) {
      const noise = Math.sin(curY * 0.12 + x + 100) * 2;
      context.lineTo(x + w + noise, curY);
    }
    
    // Close at top
    context.lineTo(x, y);
    context.closePath();

    // Fill with gradient
    const gradient = context.createLinearGradient(x, 0, x + w, 0);
    gradient.addColorStop(0, "rgba(255, 255, 255, 0.8)");     // Highlight edge
    gradient.addColorStop(0.4, "rgba(176, 224, 230, 0.6)");   // Inner body
    gradient.addColorStop(0.8, "rgba(135, 206, 250, 0.7)");   // Shadowy part
    gradient.addColorStop(1, "rgba(255, 255, 255, 0.9)");     // Edge highlight
    
    context.fillStyle = gradient;
    context.fill();
    
    // Stroke the outline
    context.strokeStyle = "rgba(255, 255, 255, 0.4)";
    context.lineWidth = 1;
    context.stroke();
    
    // Internal cracks
    context.beginPath();
    context.strokeStyle = "rgba(255, 255, 255, 0.6)";
    context.lineWidth = 1.5;
    for (let iy = 30; iy < h; iy += 50) {
      // Random-ish crack path
      const cx = x + w/2 + Math.sin(iy)*2;
      context.moveTo(x + 4, iy);
      context.lineTo(cx, iy + 10);
      context.lineTo(x + w - 4, iy + 2);
    }
    context.stroke();
    
    context.restore();
  }

  /**
   * Draws falling snow using deterministic frameTime calculations
   */
  private drawSnow(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number,
    isBackground: boolean
  ): void {
    context.save();
    context.fillStyle = "rgba(255, 255, 255, 0.8)";
    
    // Define snow parameters based on layer
    const count = isBackground ? 60 : 30;
    const baseSpeed = isBackground ? 0.03 : 0.08;
    const baseSize = isBackground ? 1.5 : 3;
    const swayAmount = isBackground ? 10 : 20;
    
    // Use a deterministic loop to draw snowflakes
    for (let i = 0; i < count; i++) {
      // Pseudo-random seed for this snowflake
      // Adding 'i' ensures each flake is unique but deterministic
      // Adding a layer offset ensures bg/fg snow are different
      const seed = i * 135.7 + (isBackground ? 0 : 9999);
      
      // Randomized properties
      const speed = baseSpeed + (Math.sin(seed) * 0.01); // Slight speed variation
      const xSeed = Math.cos(seed * 12.3) * width; // Random starting X
      const size = baseSize + Math.sin(seed * 4.5) * 0.5;
      
      // Vertical position: (Start + speed * time) % height
      // Add height to ensure positive modulo result if needed
      const y = (seed * 100 + frameTime * speed) % (height + 20) - 10;
      
      // Horizontal position: Base X + Sway
      // Sway uses sine wave based on time and seed
      const sway = Math.sin(frameTime * 0.001 + seed) * swayAmount;
      const x = (xSeed + sway + width) % width;
      
      // Draw snowflake (circle)
      context.beginPath();
      context.arc(x, y, size, 0, Math.PI * 2);
      context.fill();
    }
    
    context.restore();
  }

  /**
   * Draws animated shiny glimmers on the ice
   */
  private drawIceGlimmer(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    context.beginPath();
    
    // Moving shimmer line
    const speed = 0.05;
    const diagonalPos = (frameTime * speed) % (width + height + 200);
    
    // Create a clip region for the glimmer so it stays mostly on the frame/edges
    // (Ideally we'd use the frame shape path, but simple rect is faster)
    
    const gradient = context.createLinearGradient(
      diagonalPos - 60, 0, diagonalPos, 0
    );
    gradient.addColorStop(0, "rgba(255, 255, 255, 0)");
    gradient.addColorStop(0.5, "rgba(255, 255, 255, 0.25)");
    gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
    
    context.fillStyle = gradient;
    // Using source-atop would require drawing the shimmer only where content exists
    // GlobalCompositeOperation 'overlay' or 'screen' works well for highlights
    context.globalCompositeOperation = "screen"; 
    
    // Draw glimmer across the whole canvas
    // Rotate context for diagonal effect
    context.translate(diagonalPos, 0);
    context.rotate(Math.PI / 4);
    context.fillRect(-100, -width, 200, width * 2); // Wide diagonal strip
    
    context.restore();
  }

  /**
   * Gets icy border style - thick frozen look
   */
  getBorderStyle(): string {
    return "4px solid #B0E0E6";
  }

  /**
   * Gets icy box shadow with animated frost glow
   */
  getBoxShadow(frameTime?: number): string {
    let glowIntensity = 0.3;
    if (frameTime !== undefined) {
      glowIntensity = 0.2 + 0.15 * Math.sin(frameTime / 1500);
    }
    
    return `
      var(--shadow-lg),
      0 0 40px rgba(176, 224, 230, ${glowIntensity}),
      0 0 80px rgba(135, 206, 250, ${glowIntensity * 0.5})
    `.trim();
  }

  /**
   * Gets backdrop filter with cool tint
   */
  getBackdropFilter(): string {
    return "blur(10px) brightness(0.95)";
  }
}
